import { useCallback, useEffect, useState } from 'react'
import { apiFetch, type AppUser, type MeResponse, type Role } from '../api'
import TabAccessModal from './TabAccessModal'

const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  reader: 'Reader',
  none: 'No access',
}

function fmt(ts: string | null): string {
  if (!ts) return '—'
  const d = new Date(ts.endsWith('Z') ? ts : `${ts}Z`)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function UsersTab({ me }: { me: MeResponse }) {
  const [users, setUsers] = useState<AppUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [tabsFor, setTabsFor] = useState<AppUser | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const data = await apiFetch<{ users: AppUser[] }>('/api/users')
      setUsers(data.users ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setUsers([])
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function changeRole(user: AppUser, role: Role) {
    if (role === user.role) return
    setSavingId(user.id)
    setError(null)
    setNotice(null)
    try {
      await apiFetch(`/api/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      })
      setNotice(`${user.display_name ?? user.upn} set to ${ROLE_LABEL[role]}.`)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSavingId(null)
    }
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>Users</h2>
          <p className="panel__sub">
            Everyone who has signed in to NYMR Finance. New arrivals start with no
            access until you give them a level.
          </p>
        </div>
        <button className="btn" onClick={() => void load()}>Refresh</button>
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      {notice && <div className="alert alert--ok">{notice}</div>}

      {users === null ? (
        <p className="muted">Loading…</p>
      ) : users.length === 0 ? (
        <p className="muted">No users recorded yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Sign-in</th>
                <th>First seen</th>
                <th>Last seen</th>
                <th>Level</th>
                <th>Tabs</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.upn === me.upn
                return (
                  <tr key={u.id}>
                    <td>
                      {u.display_name ?? '—'}
                      {isSelf && <span className="tag">you</span>}
                    </td>
                    <td className="mono">{u.upn}</td>
                    <td>{fmt(u.first_seen_utc)}</td>
                    <td>{fmt(u.last_seen_utc)}</td>
                    <td>
                      <div className="level-cell">
                        <span className="role-pill" data-role={u.role}>{ROLE_LABEL[u.role]}</span>
                        <select
                          className="select"
                          value={u.role}
                          disabled={isSelf || savingId === u.id}
                          title={isSelf ? 'You cannot change your own level' : 'Set access level'}
                          onChange={(e) => void changeRole(u, e.target.value as Role)}
                        >
                          <option value="admin">Admin</option>
                          <option value="reader">Reader</option>
                          <option value="none">No access</option>
                        </select>
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn"
                        onClick={() => setTabsFor(u)}
                        title="Choose which finance tabs this user can see"
                      >
                        Tabs
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {tabsFor && (
        <TabAccessModal
          user={tabsFor}
          onClose={() => setTabsFor(null)}
          onSaved={(m) => setNotice(m)}
        />
      )}
    </section>
  )
}
